// Mounts a shader on a canvas and runs it.
// Resizes every frame (CSS-driven sizes only resolve after first layout — see
// the gotcha note in design/context/decisions.md).

const VERTEX_SRC = `attribute vec2 a; void main(){ gl_Position = vec4(a, 0., 1.); }`;

export class ShaderRunner {
  private canvas: HTMLCanvasElement;
  private frag: string;
  private gl: WebGLRenderingContext | null = null;
  private prog: WebGLProgram | null = null;
  private uT: WebGLUniformLocation | null = null;
  private uR: WebGLUniformLocation | null = null;
  private running = false;
  private raf = 0;
  private startTime = 0;
  private w = 0;
  private h = 0;
  private ok = false;
  private failed = false;

  constructor(canvas: HTMLCanvasElement, frag: string) {
    this.canvas = canvas;
    this.frag = frag;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.startTime = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      this.resize();
      this.init();
      if (this.ok) this.frame((now - this.startTime) / 1000);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  dispose(): void {
    this.stop();
    if (this.gl && this.prog) this.gl.deleteProgram(this.prog);
    this.prog = null;
    this.gl = null;
    this.ok = false;
  }

  private init(): void {
    if (this.ok || this.failed) return;
    const gl =
      this.canvas.getContext("webgl", {
        antialias: false,
        preserveDrawingBuffer: false,
      }) ||
      (this.canvas.getContext("experimental-webgl") as WebGLRenderingContext | null);
    if (!gl) {
      this.failed = true;
      this.fallback();
      return;
    }
    this.gl = gl;

    const prog = this.compile(VERTEX_SRC, this.frag);
    if (!prog) {
      this.failed = true;
      this.fallback();
      return;
    }
    this.prog = prog;
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const loc = gl.getAttribLocation(prog, "a");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    this.uT = gl.getUniformLocation(prog, "u_t");
    this.uR = gl.getUniformLocation(prog, "u_r");
    gl.clearColor(0.04, 0.05, 0.06, 1);
    this.ok = true;
  }

  private compile(vs: string, fs: string): WebGLProgram | null {
    const gl = this.gl!;
    const make = (type: number, src: string): WebGLShader | null => {
      const s = gl.createShader(type);
      if (!s) return null;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.warn("[glsl]", gl.getShaderInfoLog(s));
        return null;
      }
      return s;
    };
    const v = make(gl.VERTEX_SHADER, vs);
    const f = make(gl.FRAGMENT_SHADER, fs);
    if (!v || !f) return null;
    const p = gl.createProgram();
    if (!p) return null;
    gl.attachShader(p, v);
    gl.attachShader(p, f);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      console.warn("[link]", gl.getProgramInfoLog(p));
      return null;
    }
    return p;
  }

  private fallback(): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#0a0c0e";
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.fillStyle = "#8fffd4";
    ctx.font = "11px monospace";
    ctx.fillText("// webgl unavailable", 12, 22);
  }

  private resize(): void {
    const c = this.canvas;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const r = c.getBoundingClientRect();
    const w = Math.max(1, Math.floor(r.width * dpr));
    const h = Math.max(1, Math.floor(r.height * dpr));
    if (w === this.w && h === this.h) return;
    c.width = w;
    c.height = h;
    this.w = w;
    this.h = h;
    if (this.gl) this.gl.viewport(0, 0, w, h);
  }

  private frame(t: number): void {
    const gl = this.gl!;
    gl.useProgram(this.prog);
    gl.viewport(0, 0, this.w, this.h);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (this.uT) gl.uniform1f(this.uT, t);
    if (this.uR) gl.uniform2f(this.uR, this.w, this.h);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}

// Try-compile a fragment shader on a throwaway canvas; returns an error
// message or null. Keeps a malformed shader from corrupting the visible canvas.
export function tryCompile(src: string): string | null {
  const c = document.createElement("canvas");
  c.width = 16;
  c.height = 16;
  const gl =
    c.getContext("webgl") || (c.getContext("experimental-webgl") as WebGLRenderingContext | null);
  if (!gl) return "no webgl";
  const sh = gl.createShader(gl.FRAGMENT_SHADER);
  if (!sh) return "could not create shader";
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    return (gl.getShaderInfoLog(sh) || "compile failed").trim();
  }
  return null;
}
